// contexts/PlanOperationsContext.tsx
/**
 * PlanOperationsContext - Centralizes plan mutation handlers
 *
 * Eliminates prop drilling of course, semester, placeholder, and bookmark
 * operation handlers through CoursePlannerColumn -> CoursePlanner ->
 * SemesterGrid/BookmarkedCourses.
 *
 * Usage:
 *   // Provider (in CoursePlannerColumn):
 *   <PlanOperationsProvider value={{ onRemoveCourse, ... }}>
 *
 *   // Consumer (in SemesterGrid, BookmarkedCourses):
 *   const { onRemoveCourse, onMoveCourse, ... } = usePlanOperations();
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { EnrolledCourse } from '../types';
import type { PlaceholderData } from '../hooks/useModals';
import type { BookmarkCourseInput } from '../hooks/academicPlan/useBookmarkOperations';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Shape passed through PlanOperationsContext. Bundles a mix of:
 *   - direct planOps mutations from useAcademicPlan/usePlanChangeHandlers
 *   - UI-state toggles from useUIState
 *   - inline closures in App.js that route to modalOps
 * The signatures match what consumers (SemesterGrid, BookmarkedCourses)
 * actually call.
 */
export interface PlanOperationsContextValue {
  onRemoveCourse: (semester: string, index: number, skipConfirmation?: boolean) => Promise<void> | void;
  onMoveCourse: (
    course: EnrolledCourse,
    fromSemester: string,
    toSemester: string,
    fromIndex: number
  ) => Promise<void> | void;
  onMoveToSemester: (course: EnrolledCourse, fromSemester: string, fromIndex: number) => void;
  onToggleBookmark: (course: EnrolledCourse) => void;
  onToggleSemester: (semester: string) => void;
  onClearSemester: (semester: string) => Promise<void> | void;
  onDeleteSemester: (semester: string) => Promise<void> | void;
  onAddPlaceholder: (semester?: string) => void;
  onEditPlaceholder: (placeholder: PlaceholderData) => void;
  onConvertPlaceholder: (placeholder: PlaceholderData, semester: string, index: number) => void;
  checkCourseAvailability?: (courseCode: string, semester: string) => Promise<unknown>;
  onRemoveFromBookmarks: (index: number) => void;
  onAddToBookmarks: (course: BookmarkCourseInput) => Promise<void> | void;
  onAddCourseFromBookmark: (course: EnrolledCourse, targetSemester: string) => Promise<void> | void;
}

interface PlanOperationsProviderProps {
  value: PlanOperationsContextValue;
  children: ReactNode;
}

const PlanOperationsContext = createContext<PlanOperationsContextValue | null>(null);
PlanOperationsContext.displayName = 'PlanOperationsContext';

export const usePlanOperations = (): PlanOperationsContextValue => {
  const context = useContext(PlanOperationsContext);
  if (!context) {
    throw new Error('usePlanOperations must be used within a PlanOperationsProvider');
  }
  return context;
};

export const PlanOperationsProvider = ({ value, children }: PlanOperationsProviderProps) => {
  return (
    <PlanOperationsContext.Provider value={value}>
      {children}
    </PlanOperationsContext.Provider>
  );
};

export default PlanOperationsContext;
